import { SearchFilterSortUpload, Typography } from '@components';
import { content } from '@content';
import '@glidejs/glide/dist/css/glide.core.min.css';
import { Box } from '@material-ui/core';
import { DataVisualizationListItemSchema, ModulesSearchParams, dataVisualization } from '@modules';
import { INITIAL_QUERY, RUN_SEARCH_DELAY } from './DataVisualizationLibraryPanel.const';
import { useAppDispatch } from '@store';
import { useHistory } from '@utils';
import { KeyboardEvent, useCallback, useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { DataVisualizationLibraryProps } from './DataVisualizationLibraryPanel.props';
import { useStyles } from './DataVisualizationLibraryPanel.styles';
import { UploadVisualizationModal, UploadVisualizationModalProps } from '@routes';
import LoadingBlockPlaceholder from '@components/LoadingBlockPlaceholder/LoadingBlockPlaceholder';
import { SearchFormProps } from '@components/SearchFilterSortUpload/SearchFilterSortUpload.props';

import InfiniteScroll from 'react-infinite-scroll-component';

import useDebounceLast from '@utils/hooks/useDebounce';

export const DataVisualizationLibraryPanel = ({
  chosenDV,
  isDVLibrary = false,
  onDVSelect,
  setChosenDV,
  searchKeyword = '',
  setSearchKeyword,
}: DataVisualizationLibraryProps): JSX.Element => {
  const styles = useStyles();
  const dispatch = useAppDispatch();

  const { businessUnit } = useHistory().query;

  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [chosenDVId, setChosenDVId] = useState<number>();
  const [showUploadDataVisualizationModal, setShowUploadDataVisualizationModal] = useState(false);

  const contentContainer = useRef<null | HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loaderRowCount, setLoaderRowCount] = useState(1);

  const [dataVisualizationItems, setDataVisualizationItems] = useState<DataVisualizationListItemSchema[]>([]);

  // Add a ref map for DV elements
  const dataVisualizationRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const prevChosenDVSrc = useRef<string | undefined | null>(undefined);

  const { control, setValue, getValues } = useForm<SearchFormProps>({
    defaultValues: {
      searchType: 'dataVisual',
      keyword: searchKeyword,
      sortBy: 'name',
    },
  });

  const [query, setQuery] = useState<ModulesSearchParams>({
    ...INITIAL_QUERY,
    businessUnitId: businessUnit as number,
  });

  const dataVisualizationListData = dataVisualization.useDataVisualizationListData();

  // useLoader(dataVisualization.useDataVisualizationListMeta(), dataVisualization.useEntityMeta());

  const getDataVisualizations = useCallback(
    (keyword: string, page: number = query.page) => {
      if (businessUnit) {
        // setValue('keyword', keyword);
        setIsLoading(true);

        return dispatch(dataVisualization.thunk.getListDataVisualization({ ...query, keyword, businessUnit, page }));
      }
    },
    [query, businessUnit, dispatch],
  );

  // Note: this is resetting the data visualizations list when the component is mounted
  // so it may need to be disable
  useEffect(() => {
    // Only run on mount
    // reset library and get images
    // dispatch(dataVisualization.actions.resetDataVisualizationList());
    // getDataVisualizations('', 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [debouncedSearch] = useDebounceLast(async (keyword: string, page: number) => {
    await getDataVisualizations(keyword, page);
  }, RUN_SEARCH_DELAY);

  const handleKeywordChange = useCallback(
    (keyword: string) => {
      if (getValues().keyword !== keyword) {
        dispatch(dataVisualization.actions.resetDataVisualizationList());
        setValue('keyword', keyword);
        setQuery((prevState) => ({ ...prevState, keyword, page: 1 }));

        if (setSearchKeyword) {
          setSearchKeyword(keyword);
        }

        // runSearch(keyword, 1);
        debouncedSearch(keyword, 1).catch(() => {
          /* Handle error if needed */
        });
      }
    },
    [getValues, dispatch, setValue, setSearchKeyword, debouncedSearch],
  );

  const handleSearchKeyUp = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        handleKeywordChange(getValues().keyword);
      }
    },
    [getValues, handleKeywordChange],
  );

  const loadMoreDataVisualizations = () => {
    const nextPage = query.page + 1;
    const pageCount = dataVisualizationListData.pageCount || 0;

    if (nextPage <= pageCount) {
      setQuery((prevState) => {
        return { ...prevState, page: prevState.page + 1 };
      });

      getDataVisualizations(getValues().keyword, nextPage);
    }
  };

  // populate the data visualizations on first load for content module editor view
  useEffect(() => {
    if (getValues().keyword === '' && (dataVisualizationListData.items?.length ?? 0) === 0 && !isLoading) {
      getDataVisualizations('', 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUploadDataVisualizationModalOpen = useCallback(() => {
    setShowUploadDataVisualizationModal(true);
  }, [setShowUploadDataVisualizationModal]);

  const handleUploadDataVisualizationModalClose = useCallback(() => {
    setShowUploadDataVisualizationModal(false);
  }, [setShowUploadDataVisualizationModal]);

  const handleDataVisualizationFormSubmit: UploadVisualizationModalProps['onSubmit'] = useCallback(
    async (values) => {
      const valuesWithClient = { ...values, 'businessUnit[id]': businessUnit };
      valuesWithClient.isMaxScaleEnabled = (valuesWithClient.isMaxScaleEnabled ? 1 : 0) as any;

      const formData = new FormData();

      Object.keys(valuesWithClient).forEach((key) => {
        formData.append(key, valuesWithClient[key as keyof typeof valuesWithClient] as string | Blob);
      });

      const result = await dispatch(dataVisualization.thunk.create(formData));

      if (dataVisualization.thunk.create.fulfilled.match(result)) {
        setTimeout(() => {
          dispatch(
            dataVisualization.thunk.getListDataVisualization({
              keyword: '',
              businessUnit: businessUnit!,
            }),
          );
        }, 100);
        handleUploadDataVisualizationModalClose();
      }
    },
    [businessUnit, dispatch, handleUploadDataVisualizationModalClose],
  );

  const handleChooseDV = useCallback(
    (item: DataVisualizationListItemSchema) => () => {
      setChosenDVId(item.id);
      // const imageRatio = roundByRank(Number(item.width) / Number(item.height), 10000);
      // const width = item.width > 300 ? 300 : item.width;
      const curItem = {
        ...item,
        width: item.width,
        // height: roundByRank(Number(width) / imageRatio, 1)
        height: item.height,
      };

      setChosenDV(curItem);
      onDVSelect(curItem);
    },
    [onDVSelect, setChosenDV],
  );

  useEffect(() => {
    if (chosenDV?.thumbnailPath && prevChosenDVSrc.current !== chosenDV?.thumbnailPath) {
      const curDV = dataVisualizationListData.items?.find((dv) => dv.id === chosenDV?.id);
      setChosenDVId(curDV?.id ?? 0);

      if (curDV && dataVisualizationRefs.current[curDV.id]) {
        dataVisualizationRefs.current[curDV.id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      prevChosenDVSrc.current = chosenDV?.thumbnailPath as string;
    }
  }, [chosenDV, dataVisualizationListData.items, setChosenDVId]);

  // useEffect(() => {
  //   setDataVisualizationItems([]);
  //   getDataVisualizations('');

  //   return () => {
  //     setDataVisualizationItems([]);
  //   };
  // }, [dispatch, getDataVisualizations, setDataVisualizationItems]);

  // useEffect(() => {
  //   setDataVisualizationItems([]);
  // }, [dataVisualizationListData.items]);

  // get each images height and width on load and make sure they are ordered by name
  useEffect(() => {
    const DVs = JSON.parse(JSON.stringify(dataVisualizationListData.items ?? [])) as DataVisualizationListItemSchema[];

    if (dataVisualizationListData.items) {
      setIsLoading(false);
    }

    let loadedCount = 0;
    const processed: DataVisualizationListItemSchema[] = [];

    DVs.forEach((dataVisual, idx) => {
      if (dataVisual?.thumbnailPath) {
        const image = new Image();

        image.onload = function () {
          dataVisual.width = image.width;
          dataVisual.height = image.height;
          dataVisual.thumbnailPath = `${dataVisual.thumbnailPath}?${new Date().getTime()}`;
          processed[idx] = dataVisual;
          loadedCount++;
          if (loadedCount === DVs.length) {
            // All images processed, sort and set
            setDataVisualizationItems([...processed].sort((a, b) => (a.name || '').localeCompare(b.name || '')));

            // scroll selected DV into view
            if (isInitialLoad) {
              if (chosenDV && dataVisualizationRefs.current[chosenDV.id]) {
                dataVisualizationRefs.current[chosenDV.id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
              setIsInitialLoad(false);
            }
          }
        };
        image.onerror = () => {
          processed[idx] = dataVisual;
          loadedCount++;
          if (loadedCount === DVs.length) {
            setDataVisualizationItems([...processed].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
          }
        };

        image.src = dataVisual.thumbnailPath as string;
      } else {
        processed[idx] = dataVisual;
        loadedCount++;
        if (loadedCount === DVs.length) {
          setDataVisualizationItems([...processed].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
        }
      }
    });
  }, [chosenDV, dataVisualizationListData.items]);

  useEffect(() => {
    if (contentContainer.current) {
      const containerHeight = contentContainer.current?.offsetHeight;
      const LOADER_HEIGHT_WITH_MARGIN = 255;
      setLoaderRowCount(() => Math.ceil(containerHeight / LOADER_HEIGHT_WITH_MARGIN));
    }
  }, [contentContainer]);

  return (
    <Box className={styles.DVLibraryPanel} {...{ ref: contentContainer }}>
      <Box className={styles.searchBar}>
        <SearchFilterSortUpload
          className={styles.sfsuContainer}
          control={control}
          onSearchTermChange={handleKeywordChange}
          onKeyUp={handleSearchKeyUp}
          onUploadClick={handleUploadDataVisualizationModalOpen}
          placeholder={content.search}
          searchBarClassName={styles.sfsuSearchBar}
          showUpload={true}
          uploadButtonClassName={styles.sfsuUploadButton}
        />
      </Box>
      {showUploadDataVisualizationModal && (
        <UploadVisualizationModal
          onSubmit={handleDataVisualizationFormSubmit}
          onCancel={handleUploadDataVisualizationModalClose}
        />
      )}
      <Box className={styles.content} id="data-visualization-library-scrollable-div">
        <InfiniteScroll
          next={loadMoreDataVisualizations}
          loader=""
          hasMore={(dataVisualizationListData.currentPage ?? 0) < (dataVisualizationListData.pageCount ?? 0)}
          dataLength={dataVisualizationListData.items?.length ?? 0}
          scrollableTarget="data-visualization-library-scrollable-div"
        >
          {isLoading &&
            (dataVisualizationListData.items?.length ?? 0) === 0 &&
            [...Array(loaderRowCount)].map((_: number, index: number) => (
              <Box key={`loaderRow-${index}`} className={styles.loaderBlock}>
                <LoadingBlockPlaceholder />
              </Box>
            ))}
          {dataVisualizationItems.map((item) => (
            <div
              key={`${item.name}-${item.id}`}
              data-chosen={chosenDVId! === item.id}
              className={styles.imageWrapper}
              ref={(el) => (dataVisualizationRefs.current[item.id] = el)}
              onClick={handleChooseDV(item)}
            >
              <div
                id={String(item.id)}
                className={styles.image}
                style={{ backgroundImage: `url(${item.thumbnailPath})` }}
              />
              <Box className={styles.imageContent}>
                <Box className={styles.moduleRow}>
                  <Typography.Headline className={styles.name}>{item.name}</Typography.Headline>
                </Box>
                <Box className={styles.moduleRow}>
                  <Typography.SmallCaption className={styles.index}>
                    {`${Math.round(item.width)}x${Math.round(item.height)}`}
                  </Typography.SmallCaption>
                </Box>
              </Box>
            </div>
          ))}
        </InfiniteScroll>
      </Box>
    </Box>
  );
};
